import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { API } from "../constant/apiConstant";
import { setAdmin, logout } from "../features/auth/authSlice";
import { Colors } from "../utils/colors";

const guardStyles = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  gap: 2,
  color: "#fff",
  backgroundColor: Colors.secondary
};

export default function AdminRouteGuard({
  children,
  allowDirectRender = false,
  allowedRoles = null
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    authorized: false,
    role: "",
    assignedDrawId: null
  });

  useEffect(() => {
    let isMounted = true;

    const verifySession = async () => {
      try {
        const response = await fetch(`${API.BASE_URL}/api/admin/profile`, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          }
        });

        const data = await response.json().catch(() => null);

        if (!isMounted) {
          return;
        }

        if (response.ok && data?.status && data?.admin) {
          dispatch(setAdmin(data.admin.role));
          setState({
            loading: false,
            authorized: true,
            role: data.admin.role || "",
            assignedDrawId: data.admin.assigned_draw_id || null
          });
          return;
        }
      } catch (_error) {
        // Route guard should fail closed without exposing internal details.
      }

      if (!isMounted) {
        return;
      }

      dispatch(logout());
      setState({
        loading: false,
        authorized: false,
        role: "",
        assignedDrawId: null
      });
    };

    verifySession();

    return () => {
      isMounted = false;
    };
  }, [dispatch, location.pathname]);

  if (state.loading) {
    return (
      <Box sx={guardStyles}>
        <CircularProgress sx={{ color: "#ffcc33" }} />
        <Typography variant="body2">Checking admin session...</Typography>
      </Box>
    );
  }

  if (!state.authorized) {
    return <Navigate to="/" replace state={{ from: location, denied: true }} />;
  }

  if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    const normalizedRole = String(state.role || "").trim().toLowerCase();
    const isAllowed = allowedRoles.some(
      (role) => String(role || "").trim().toLowerCase() === normalizedRole
    );

    if (!isAllowed) {
      if (normalizedRole === "presenter" && state.assignedDrawId) {
        return <Navigate to={`/admin/draws/live/${state.assignedDrawId}`} replace />;
      }

      return <Navigate to="/admin/users" replace />;
    }
  }

  if (children) {
    return children;
  }

  if (allowDirectRender) {
    return <Outlet />;
  }

  return <Outlet />;
}
